import { useState, useEffect } from 'react'
import { Package, CheckCircle, Truck, Settings, AlertTriangle, Clock, Euro, Shield, Warehouse, Plus } from 'lucide-react'
import ReturnsListTab from '@/components/returns/ReturnsListTab'
import ReturnsConfigTab from '@/components/returns/ReturnsConfigTab'
import ReturnsBlacklistTab from '@/components/returns/ReturnsBlacklistTab'
import CreateIncidentModal from '@/components/incidents/CreateIncidentModal'
import api from '@/lib/api'
import { formatPrice } from '@/lib/utils'

type TabType = 'all' | 'received' | 'pending_refund' | 'completed' | 'blacklist' | 'config'

interface TabConfig {
  id: TabType
  label: string
  icon: React.ElementType
  statusFilter?: string
  description: string
  showRefundMethodChips?: boolean
}

interface Stats {
  pending: number
  today: number
  total: number
  total_refunded: number
  by_status: Record<string, number>
  tested_by_refund_method?: Record<string, number>
}

const TABS: TabConfig[] = [
  {
    id: 'all',
    label: 'Todas',
    icon: Package,
    description: 'Todas las devoluciones'
  },
  {
    id: 'received',
    label: 'En almacén',
    icon: Warehouse,
    statusFilter: 'received',
    description: 'Recibidas pendientes de testear'
  },
  {
    id: 'pending_refund',
    label: 'Por reembolsar',
    icon: Euro,
    statusFilter: 'tested',
    showRefundMethodChips: true,
    description: 'Testeadas pendientes de reembolso'
  },
  {
    id: 'completed',
    label: 'Completadas',
    icon: CheckCircle,
    statusFilter: 'refunded,replacement,refund_rejected',
    description: 'Devoluciones finalizadas'
  },
  {
    id: 'blacklist',
    label: 'Exclusiones',
    icon: Shield,
    description: 'Clientes excluidos de recogida'
  },
  {
    id: 'config',
    label: 'Ajustes',
    icon: Settings,
    description: 'Configuración del módulo'
  }
]

export default function ReturnsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [stats, setStats] = useState<Stats | null>(null)
  const [manualReturnOpen, setManualReturnOpen] = useState(false)
  const [listReloadKey, setListReloadKey] = useState(0)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const response = await api.get('/returns/admin/stats?case_type=return')
      setStats(response.data)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  const getTabCount = (tabId: TabType): number | undefined => {
    if (!stats) return undefined
    switch (tabId) {
      case 'all':
        return stats.total
      case 'received':
        return stats.by_status?.received || 0
      case 'pending_refund':
        return stats.by_status?.tested || 0
      case 'completed':
        return (stats.by_status?.refunded || 0) + (stats.by_status?.replacement || 0) + (stats.by_status?.refund_rejected || 0)
      default:
        return undefined
    }
  }

  const currentTab = TABS.find(t => t.id === activeTab)

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="px-6">
          <div className="py-4 border-b border-slate-100 dark:border-slate-700/50">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Devoluciones</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Gestión de solicitudes de devolución y reembolsos
                </p>
              </div>
              <button
                onClick={() => setManualReturnOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium shadow-sm"
                title="Dar de alta una devolución saltándose la validación de plazo"
              >
                <Plus className="w-4 h-4" />
                Nueva devolución manual
              </button>
            </div>

            {/* KPI Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 leading-none">{stats.by_status?.requested || 0}</p>
                    <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-0.5">Pendientes</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200/60 dark:border-blue-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                    <Truck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 leading-none">
                      {(stats.by_status?.approved || 0) + (stats.by_status?.pickup_scheduled || 0) + (stats.by_status?.in_transit || 0) + (stats.by_status?.received || 0) + (stats.by_status?.tested || 0)}
                    </p>
                    <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-0.5">En proceso</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200/60 dark:border-indigo-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg">
                    <Clock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 leading-none">{stats.today}</p>
                    <p className="text-xs text-indigo-600/80 dark:text-indigo-400/80 mt-0.5">Hoy</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/40 rounded-xl px-4 py-3">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg">
                    <Euro className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 leading-none">{formatPrice(stats.total_refunded)}</p>
                    <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80 mt-0.5">Reembolsado</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 pt-2 overflow-x-auto scrollbar-hide">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              const count = getTabCount(tab.id)

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2.5 border-b-2 transition-all whitespace-nowrap text-sm ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 font-semibold'
                      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 font-medium'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-500' : ''}`} />
                  {tab.label}
                  {count !== undefined && count > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.5 text-[11px] font-semibold rounded-full leading-none ${
                      isActive
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="h-[calc(100vh-245px)]">
        {activeTab === 'config' ? (
          <ReturnsConfigTab />
        ) : activeTab === 'blacklist' ? (
          <ReturnsBlacklistTab />
        ) : (
          <ReturnsListTab
            key={listReloadKey}
            statusFilter={currentTab?.statusFilter}
            showRefundMethodChips={currentTab?.showRefundMethodChips}
            onStatsChange={loadStats}
            caseType="return"
          />
        )}
      </div>

      <CreateIncidentModal
        isOpen={manualReturnOpen}
        onClose={() => setManualReturnOpen(false)}
        onSuccess={() => {
          setManualReturnOpen(false)
          loadStats()
          setListReloadKey((k) => k + 1)
        }}
        forceCaseType="return"
      />
    </div>
  )
}
